import { IconMathGreaterThanCircle } from "central-icons/IconMathGreaterThanCircle";
import { Text } from "@/components/podkit/typography/Text";
import { useEnvironment } from "@/queries/environment-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import {
    type EnvironmentGitStatus,
    type FileChange,
    FileChange_ChangeType,
} from "gitpod-next-api/gitpod/v1/environment_pb";
import { useId, useCallback, type FC } from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/components/podkit/lib/cn";
import { pluralize } from "@/utils/strings";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { Notification } from "@/components/flexkit/badge/Notification";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { Link } from "react-router-dom";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Button } from "@/components/flexkit/Button";
import { Check, Copy } from "lucide-react";
import { useTemporaryState } from "@/hooks/use-temporary-value";

type GitStatusPanelProps = {
    environmentId: string;
};

export const GitStatusPanel: FC<GitStatusPanelProps> = ({ environmentId }) => {
    const { data: environment } = useEnvironment(environmentId);
    const gitStatus = environment?.status?.content?.git;
    const { toast } = useToast();
    const [copied, setCopied] = useTemporaryState(false, 2000);

    const handleCopyBranch = useCallback(async () => {
        if (!gitStatus?.branch) return;
        try {
            await navigator.clipboard.writeText(gitStatus.branch);
            setCopied(true);
            toast({ title: "Branch copied to clipboard", description: gitStatus.branch });
        } catch {
            // swallow; clipboard may be unavailable in some contexts
        }
    }, [gitStatus?.branch, toast, setCopied]);

    if (!gitStatus) {
        return null;
    }

    return (
        <div className="rounded-xl border-0.5 border-border-base bg-surface-secondary px-0.5 py-1.5 transition-shadow hover:shadow-md">
            <ScrollArea orientation="vertical" className="max-h-[400px]">
                <div className="flex w-full items-center justify-between rounded-xl px-[18px] py-2.5">
                    <span className="mr-2 select-none text-start text-md font-medium">Git Status</span>
                    {gitStatus.totalChangedFiles > 0 && (
                        <Notification
                            className="flex min-h-6 select-none items-center truncate"
                            content={`${gitStatus.totalChangedFiles} ${pluralize(gitStatus.totalChangedFiles, "change")}`}
                        />
                    )}
                </div>
                <div className="px-5 pb-2">
                    <Text className="select-none font-mono text-base text-content-muted">Branch</Text>
                    <div className="flex items-center gap-2 pb-2">
                        <Text className="text-base text-content-primary">{gitStatus.branch}</Text>
                        <Button
                            variant="ghost"
                            type="button"
                            className="h-6 rounded-lg border-none p-1 text-content-tertiary hover:text-content-secondary hover:opacity-100"
                            onClick={handleCopyBranch}
                            aria-label={`Copy branch ${gitStatus.branch}`}
                            data-tracking-id="copy-branch-git-status-panel"
                        >
                            {copied ? (
                                <Check className="text-content-success" aria-hidden size={16} />
                            ) : (
                                <Copy aria-hidden size={16} />
                            )}
                        </Button>
                    </div>
                    <UnpushedCommitsSection gitStatus={gitStatus} />
                    {gitStatus.changedFiles.length > 0 ? (
                        <>
                            <hr className="my-2 border-border-subtle" />
                            <UncommitedFilesSection gitStatus={gitStatus} />
                        </>
                    ) : null}
                </div>
            </ScrollArea>
        </div>
    );
};

const gitStatusDotVariants = cva(
    "flex size-4 items-center justify-center rounded-full bg-surface-accent text-sm font-medium select-none",
    {
        variants: {
            count: {
                false: null,
                true: "bg-surface-accent text-content-muted",
            },
            status: {
                [FileChange_ChangeType.UNSPECIFIED]: null,
                [FileChange_ChangeType.MODIFIED]: "bg-surface-accent text-content-muted",
                [FileChange_ChangeType.DELETED]: "bg-surface-warning-subtle text-content-destructive",
                [FileChange_ChangeType.ADDED]: "bg-surface-success-subtle text-content-success",
                [FileChange_ChangeType.UNTRACKED]: "bg-surface-warning-subtle text-content-warning",
                [FileChange_ChangeType.RENAMED]: "bg-surface-success-subtle text-content-success",
                [FileChange_ChangeType.COPIED]: "bg-surface-success-subtle text-content-success",
                [FileChange_ChangeType.UPDATED_BUT_UNMERGED]: "bg-surface-warning-subtle text-content-warning",
            },
        },
    },
);

const CHANGE_TYPE_LABEL = {
    [FileChange_ChangeType.UNSPECIFIED]: null,
    [FileChange_ChangeType.MODIFIED]: { symbol: "M", full: "modified" },
    [FileChange_ChangeType.DELETED]: { symbol: "D", full: "deleted" },
    [FileChange_ChangeType.ADDED]: { symbol: "A", full: "added" },
    [FileChange_ChangeType.UNTRACKED]: { symbol: "U", full: "untracked" },
    [FileChange_ChangeType.RENAMED]: { symbol: "R", full: "renamed" },
    [FileChange_ChangeType.COPIED]: { symbol: "C", full: "copied" },
    [FileChange_ChangeType.UPDATED_BUT_UNMERGED]: { symbol: "!", full: "unmerged" },
} as const;

export const GitStatusDot: FC<{ count: number } | { status: FileChange_ChangeType }> = (props) => {
    if ("count" in props) {
        return (
            <span aria-hidden="true" className={cn(gitStatusDotVariants({ count: true }))}>
                {props.count}
            </span>
        );
    }

    const label = CHANGE_TYPE_LABEL[props.status];
    if (!label) return null;

    return (
        <span aria-hidden="true" className={cn(gitStatusDotVariants({ status: props.status }))}>
            {label.symbol}
        </span>
    );
};

export const UnpushedCommitsSection: FC<{
    gitStatus: PlainMessage<EnvironmentGitStatus>;
}> = ({ gitStatus }) => {
    const headerId = useId();

    if (!gitStatus.unpushedCommits || gitStatus.unpushedCommits.length === 0) {
        return null;
    }

    return (
        <>
            <div className="flex items-center justify-between gap-2 py-2">
                <Text id={headerId} className="select-none font-mono text-base text-content-muted">
                    Unpushed commits
                </Text>
                <GitStatusDot count={gitStatus.unpushedCommits.length} />
                <span className="sr-only">
                    {gitStatus.unpushedCommits.length} unpushed {pluralize(gitStatus.unpushedCommits.length, "commit")}
                </span>
            </div>
            <ul className="space-y-2 pb-2" aria-labelledby={headerId}>
                {gitStatus.unpushedCommits.map((commit) => (
                    <li key={commit} className="flex items-center gap-2 text-base text-content-primary">
                        <span className="flex size-4 shrink-0 items-center justify-center">
                            <IconMathGreaterThanCircle size="sm" />
                        </span>
                        {commit}
                    </li>
                ))}
            </ul>
        </>
    );
};

export const UncommitedFilesSection: FC<{
    gitStatus: PlainMessage<EnvironmentGitStatus>;
}> = ({ gitStatus }) => {
    const headerId = useId();
    return (
        <>
            <div className="flex items-center justify-between gap-2 py-2">
                <Text id={headerId} className="select-none font-mono text-base text-content-muted">
                    Uncommitted files
                </Text>
                <GitStatusDot count={gitStatus.changedFiles.length} />
                <span className="sr-only">
                    {gitStatus.changedFiles.length} uncommitted {pluralize(gitStatus.changedFiles.length, "file")}
                </span>
            </div>
            <ul className="space-y-2 pb-2" aria-labelledby={headerId}>
                {gitStatus.changedFiles.map((file) => (
                    <UncommitedFileItem key={file.path} file={file} />
                ))}
            </ul>
        </>
    );
};

export const UncommitedFileItem: FC<{
    file: PlainMessage<FileChange>;
}> = ({ file }) => {
    const fileName = file.path.split("/").at(-1);
    const { isMobileViewport } = useIsMobileViewport();
    return (
        <li className="flex items-center gap-2 text-base text-content-primary">
            <span className="flex size-4 shrink-0 items-center justify-center">
                <IconMathGreaterThanCircle size="sm" />
            </span>
            {isMobileViewport ? (
                <Link
                    className="flex-1 truncate font-mono text-base underline"
                    to={{ pathname: "./code-changes", hash: file.path }}
                >
                    {fileName}
                </Link>
            ) : (
                <Text className="flex-1 truncate font-mono text-base">{fileName}</Text>
            )}
            <Text className="flex-1 truncate font-mono text-base text-content-muted">{file.path}</Text>
            <GitStatusDot status={file.changeType} />
            {CHANGE_TYPE_LABEL[file.changeType] ? (
                <span className="sr-only">{CHANGE_TYPE_LABEL[file.changeType]!.full}</span>
            ) : null}
        </li>
    );
};
